import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Calendar, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

export default function StylistSchedulePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - start.getDay() + 1 + weekOffset * 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const startStr = start.toISOString().slice(0, 10);
      const endStr = end.toISOString().slice(0, 10);

      const [apptRes, shiftRes] = await Promise.all([
        api.get(`/appointments?provider_id=${user.user_id}&start_date=${startStr}T00:00:00&end_date=${endStr}T23:59:59`),
        api.get(`/shifts?start_date=${startStr}&end_date=${endStr}&user_id=${user.user_id}`),
      ]);
      const appts = (apptRes.data || []).filter(a => a.status !== "cancelled");
      const shifts = shiftRes.data || [];

      // Group by day
      const days = {};
      const shiftsByDay = {};
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().slice(0, 10);
        days[key] = [];
        shiftsByDay[key] = [];
      }
      appts.forEach(a => {
        const key = a.start_time?.slice(0, 10);
        if (days[key]) days[key].push(a);
      });
      shifts.forEach(s => {
        if (shiftsByDay[s.date]) shiftsByDay[s.date].push(s);
      });

      setData({ days, shiftsByDay, startStr, endStr, start: new Date(start), end: new Date(end) });
    } catch {
      toast.error("Failed to load schedule");
    }
    setLoading(false);
  }, [weekOffset, user.user_id]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }); }
    catch { return ""; }
  };

  const fmtDay = (dateStr) => {
    try {
      const d = new Date(dateStr + "T12:00:00");
      return { day: d.toLocaleDateString("en-US", { weekday: "short" }), date: d.getDate(), month: d.toLocaleDateString("en-US", { month: "short" }) };
    } catch { return { day: "", date: "", month: "" }; }
  };

  const isToday = (dateStr) => dateStr === new Date().toISOString().slice(0, 10);

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;
  }

  return (
    <div data-testid="stylist-schedule-page" className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">My Schedule</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w - 1)} className="border-slate-200" data-testid="prev-week-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)} className="border-slate-200 text-xs" data-testid="today-btn">Today</Button>
          <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w + 1)} className="border-slate-200" data-testid="next-week-btn"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {data?.start && (
        <p className="text-sm text-slate-400">
          {data.start.toLocaleDateString("en-US", { month: "long", day: "numeric" })} — {data.end?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
      )}

      <div className="space-y-4">
        {Object.entries(data?.days || {}).map(([dateStr, appts]) => {
          const { day, date, month } = fmtDay(dateStr);
          const today = isToday(dateStr);
          const dayShifts = (data?.shiftsByDay || {})[dateStr] || [];
          return (
            <div key={dateStr} data-testid={`day-${dateStr}`} className={`bg-white border rounded-2xl overflow-hidden ${today ? "border-rose-300 ring-1 ring-[#B5A493]/30" : "border-slate-200"}`}>
              <div className={`px-4 py-2.5 flex items-center justify-between ${today ? "bg-[#FAFAFA]" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center text-xs font-semibold ${today ? "bg-slate-900 text-white" : "bg-[#FAFAFA] text-slate-600"}`}>
                    <span className="text-[10px] leading-none">{day}</span>
                    <span className="text-sm leading-none">{date}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{month} {date}{today ? " — Today" : ""}</p>
                    {dayShifts.length > 0 && (
                      <p className="text-[11px] text-emerald-700 font-medium tabular-nums" data-testid={`shift-block-${dateStr}`}>
                        {dayShifts.map(s => `${s.start_time}–${s.end_time}`).join(" · ")}
                        {dayShifts[0]?.role ? ` · ${dayShifts[0].role}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-xs text-slate-400">{appts.length} appt{appts.length !== 1 ? "s" : ""}</span>
              </div>
              {appts.length === 0 ? (
                <div className="px-4 py-4 text-center text-sm text-rose-400">No appointments</div>
              ) : (
                <div className="divide-y divide-[#F0EFED]">
                  {appts.sort((a, b) => a.start_time?.localeCompare(b.start_time)).map((appt) => (
                    <div key={appt.appointment_id} className="px-4 py-3 flex items-center gap-4">
                      <div className="min-w-[55px] text-center">
                        <p className="text-xs font-semibold text-slate-900">{fmtTime(appt.start_time)}</p>
                        <p className="text-[10px] text-slate-400">{fmtTime(appt.end_time)}</p>
                      </div>
                      <div className="w-px h-8 bg-[#E6E4E0]" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{appt.client_name}</p>
                        <p className="text-xs text-slate-400">{(appt.service_names || []).join(", ")}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">${appt.total_price}</p>
                        <p className={`text-[10px] ${appt.status === "completed" ? "text-emerald-600" : appt.status === "no-show" ? "text-rose-600" : "text-slate-400"}`}>{appt.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
